import { flightAudio } from './flight-audio';
import { browserStorage } from './durable-storage';
import { SUPPORTED_MODES } from '../shared/modes';
import { GameApp } from './app';
import { ApiError, retryAfterMs, connectWebSocket, createWebKeyboard, type Platform, type Hit } from './platform';
import { loadImage } from './image-loader';

const canvas = document.querySelector<HTMLCanvasElement>('#game')!;
const stage = document.querySelector<HTMLDivElement>('#stage')!;
const controls = document.querySelector<HTMLDivElement>('#controls')!;
const keyboard = createWebKeyboard(stage);
const profile = new URLSearchParams(location.search).get('profile') ?? 'default';
const prefix = `friend-draw:${profile}:`;
const read = <T>(key: string, fallback: T): T => { try { return JSON.parse(localStorage.getItem(prefix + key) ?? 'null') ?? fallback; } catch { return fallback; } };
function dialog(title: string, message: string, input: boolean, initial = ''): Promise<string | null> {
  return new Promise(resolve => {
    const shell = document.querySelector<HTMLDialogElement>('#dialog')!;
    const field = shell.querySelector<HTMLInputElement>('input')!;
    shell.querySelector('h2')!.textContent = title; shell.querySelector('p')!.textContent = message;
    field.hidden = !input; field.value = initial;
    shell.showModal(); if (input) field.focus();
    shell.onclose = () => resolve(shell.returnValue === 'confirm' ? input ? field.value : 'yes' : null);
  });
}
let lastControls = '';
let forwardCanvasPointer: ((kind: 'down' | 'move' | 'up' | 'cancel',event:PointerEvent)=>void)|undefined;
const audio = flightAudio(()=>new AudioContext());
const platform: Platform = {
  sound: audio, music: audio.music, audioSettings: audio.settings, unlockAudio: audio.unlock, silenceAudio: audio.pause,
  durable: browserStorage(prefix),
  canvas, width: 390, height: 780, dpr: devicePixelRatio || 1, topInset: 0,
  initialInvite: new URLSearchParams(location.search).get('invite') ?? undefined,
  initialRoom: new URLSearchParams(location.search).get('room') ?? undefined,
  connectSocket(path, handlers) { return connectWebSocket(location.origin, path, handlers); },
  openKeyboard: options => keyboard.open(options),
  closeKeyboard: () => keyboard.close(),
  loadImage: url => loadImage(url, () => new Image()),
  async request<T>(path: string, method: string, data?: unknown, token?: string, key?: string): Promise<T> {
    let response: Response;
    try { response = await fetch(`/api${path}`, {
      method, headers: { 'X-Supported-Modes': SUPPORTED_MODES, 'X-Rescue-Version': '2', 'X-Live-Version': '3', 'X-Flight-Version':'2', 'X-Art-Version': '2', ...(data === undefined ? {} : { 'Content-Type': 'application/json' }), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(key ? { 'Idempotency-Key': key } : {}) },
      body: data === undefined ? undefined : JSON.stringify(data), signal: AbortSignal.timeout(15_000),
    }); } catch { throw new ApiError(0, '网络暂时断开，请检查连接后重试。'); }
    const result = await response.json(); if (!response.ok) throw new ApiError(response.status, result.error ?? '请求失败，请重试。', retryAfterMs({ 'retry-after': response.headers.get('retry-after') })); return result as T;
  },
  async credentials() {
    let secret = read('device-secret', '');
    if (!secret) { secret = Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join(''); localStorage.setItem(prefix + 'device-secret', JSON.stringify(secret)); }
    return { provider: 'guest', secret };
  },
  chooseAvatar() {
    return new Promise<string | null>(resolve => {
      const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/png,image/jpeg';
      input.onchange = () => { const file = input.files?.[0]; if (!file) { resolve(null); return; } const reader = new FileReader(); reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null); reader.onerror = () => resolve(null); reader.readAsDataURL(file); };
      input.click();
    });
  },
  get: read,
  set(key, value) { localStorage.setItem(prefix + key, JSON.stringify(value)); },
  remove(key) { localStorage.removeItem(prefix + key); },
  clearAccount() { Object.keys(localStorage).filter(key => key.startsWith(prefix)).forEach(key => localStorage.removeItem(key)); },
  prompt: (title, message, initial) => dialog(title, message, true, initial),
  confirm: async (title, message) => (await dialog(title, message, false)) !== null,
  choose(title, options) {
    return new Promise(resolve => {
      const shell = document.createElement('dialog'); const form = document.createElement('form'); form.method = 'dialog';
      const heading = document.createElement('h2'); heading.textContent = title; form.append(heading);
      [...options, '取消'].forEach((label, index) => {
        const row = document.createElement('div'); row.className = 'dialog-actions';
        const button = document.createElement('button'); button.value = String(index); button.textContent = label; row.append(button); form.append(row);
      });
      shell.append(form); document.body.append(shell); shell.showModal();
      shell.onclose = () => { const selection = shell.returnValue === '' ? options.length : Number(shell.returnValue); shell.remove(); resolve(selection < options.length ? selection : null); };
    });
  },
  async consent() { return true; },
  async share(code, options) {
    const link = `${location.origin}/?${options?.room ? 'room' : 'invite'}=${encodeURIComponent(code)}${options?.mode?'&mode='+encodeURIComponent(options.mode):''}`;
    const message = options?.room ? `房间码：${code}` : `邀请码：${code}。复制下方链接发给好友，或让 TA 在首页输入邀请码。`;
    await dialog(options?.title ?? '把邀请寄给好友', message, true, link);
  },
  async copyText(text) {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return; }
    const field = document.createElement('textarea'); field.value = text; field.style.position = 'fixed'; field.style.opacity = '0'; document.body.append(field); field.select();
    if (!document.execCommand('copy')) throw new Error('复制失败，请长按邀请码手动复制。');
    field.remove();
  },
  async export(source) {
    const blob = await new Promise<Blob>((resolve, reject) => source.toBlob(blob => blob ? resolve(blob) : reject(new Error('图片生成失败。')), 'image/png'));
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = '友友-我的画.png'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  vibrate(strength='light') { navigator.vibrate?.(strength==='heavy'?70:strength==='medium'?40:18); },
  createCanvas(width, height) { const result = document.createElement('canvas'); result.width = width; result.height = height; return result; },
  controls(hits: Hit[], title: string) {
    const descriptor = JSON.stringify(hits.map(({ action, ...hit }) => hit));
    // Accessible DOM controls follow exactly the Canvas hit areas.
    if (descriptor !== lastControls) {
      controls.replaceChildren(); const scale = 1;
      for (const hit of hits) {
        const button = document.createElement('button'); button.className = 'hit'; button.textContent = hit.label;
        button.setAttribute('aria-label', hit.label); button.dataset.control = hit.id; button.disabled = !!hit.disabled;
        Object.assign(button.style, { left: `${hit.x * scale}px`, top: `${hit.y * scale}px`, width: `${hit.w * scale}px`, height: `${hit.h * scale}px` });
        if(hit.drag){
          button.style.touchAction='none';
          button.onpointerdown=event=>{event.preventDefault();canvas.setPointerCapture(event.pointerId);forwardCanvasPointer?.('down',event);};
          button.onkeydown=event=>{if(['ArrowLeft','ArrowDown','ArrowRight','ArrowUp'].includes(event.key)){event.preventDefault();hit.onAdjust?.(hit.onScroll?(event.key==='ArrowUp'||event.key==='ArrowLeft'?-1:1):(event.key==='ArrowLeft'||event.key==='ArrowDown'?-1:1));}else if(hit.onScroll&&['Enter',' '].includes(event.key)){event.preventDefault();hit.action();}};
          if(hit.onScroll)button.onwheel=event=>{event.preventDefault();hit.onScroll!(event.deltaY*(event.deltaMode===1?16:event.deltaMode===2?platform.height:1)/scale);};
        }else button.onclick = () => { if (title.includes("涂鸦战机") || hit.id.startsWith("flight-") || hit.id === "mode-doodle-flight") audio.unlock(); hit.action(); };
        controls.append(button);
      }
      lastControls = descriptor;
    }
    document.querySelector('#screen-status')!.textContent = title;
  },
  onPointer(handler) {
    const send = (kind: 'down' | 'move' | 'up' | 'cancel', event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect(); handler(kind, { id: event.pointerId, x: event.clientX - rect.left, y: event.clientY - rect.top });
    };
    forwardCanvasPointer=send;
    canvas.onpointerdown = event => { canvas.setPointerCapture(event.pointerId); send('down', event); };
    canvas.onpointermove = event => send('move', event); canvas.onpointerup = event => send('up', event); canvas.onpointercancel = event => send('cancel', event);
  },
  onResume(handler) { window.addEventListener('focus', () => handler()); document.addEventListener('visibilitychange', () => { if (!document.hidden) handler(); }); },
  onRoomInvite(handler) { window.addEventListener('popstate', () => { const code = new URLSearchParams(location.search).get('room'); if (code) handler(code); }); },
  onPause(handler) { document.addEventListener('visibilitychange', () => { if (document.hidden) { keyboard.close(); handler(); } }); window.addEventListener('pagehide', () => { keyboard.close(); handler(); }); },
};
let resizeHandler: (() => void) | undefined;
platform.onResize = handler => { resizeHandler = handler; };
function resize() {
  platform.width = window.innerWidth;
  platform.height = window.innerHeight;
  const style = getComputedStyle(stage);
  platform.safeInsets = { top: parseFloat(style.paddingTop) || 0, right: parseFloat(style.paddingRight) || 0, bottom: parseFloat(style.paddingBottom) || 0, left: parseFloat(style.paddingLeft) || 0 };
  platform.topInset = platform.safeInsets.top;
  platform.dpr = Math.min(devicePixelRatio || 1, 3);
  canvas.width = platform.width * platform.dpr; canvas.height = platform.height * platform.dpr;
  stage.style.width = `${platform.width}px`; stage.style.height = `${platform.height}px`;
  canvas.style.width = `${platform.width}px`; canvas.style.height = `${platform.height}px`; lastControls = ''; resizeHandler?.();
}
resize(); window.addEventListener('resize', resize);
new GameApp(platform);

if(typeof navigator!=='undefined'&&'serviceWorker' in navigator)void navigator.serviceWorker.register('/flight-sw.js').catch(()=>{});
